import React from 'react'
import {VenueSubmissionFormState} from '../../../../types'
import type {CountryCode} from 'libphonenumber-js'

type CopyState = 'idle' | 'success' | 'error'

interface CopyEmailBodySectionProps {
  formValues: VenueSubmissionFormState
  cantFindLocationEmailAddress: (
    countryCode: CountryCode
  ) => 'rent@kegstar.com' | 'collections@kegstar.com'
  cantFindLocationMailToBody: ({
    venueName,
    address,
    contactName,
    contactEmail,
    contactPhone,
    additionalNotes,
    includeKegCollection,
    collectionRequest,
  }: {
    venueName: string
    address: VenueSubmissionFormState['address']
    contactName: string
    contactEmail: string
    contactPhone: string
    additionalNotes: string | undefined
    includeKegCollection: boolean
    collectionRequest: VenueSubmissionFormState['collectionRequest']
  }) => string
  cantFindLocationEmailSubjectText: string
}

const COPY_RESET_DELAY = 3000

export default function CopyEmailBodySection(props: CopyEmailBodySectionProps) {
  const {
    formValues,
    cantFindLocationEmailAddress,
    cantFindLocationMailToBody,
    cantFindLocationEmailSubjectText,
  } = props

  const [subjectCopyState, setSubjectCopyState] = React.useState<CopyState>('idle')
  const [bodyCopyState, setBodyCopyState] = React.useState<CopyState>('idle')
  const [recipientCopyState, setRecipientCopyState] = React.useState<CopyState>('idle')

  const copyWithFeedback = React.useCallback(
    async (text: string, type: 'subject' | 'body' | 'recipient') => {
      const setState =
        type === 'subject'
          ? setSubjectCopyState
          : type === 'body'
            ? setBodyCopyState
            : setRecipientCopyState

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          // Fallback for older browsers
          const textarea = document.createElement('textarea')
          textarea.value = text
          textarea.style.position = 'fixed'
          textarea.style.left = '-9999px'
          document.body.appendChild(textarea)
          textarea.select()
          const successful = document.execCommand('copy')
          document.body.removeChild(textarea)
          if (!successful) {
            throw new Error('execCommand copy failed')
          }
        }

        setState('success')
      } catch (error) {
        console.error('Failed to copy to clipboard:', error)
        setState('error')
      } finally {
        window.setTimeout(() => {
          setState('idle')
        }, COPY_RESET_DELAY)
      }
    },
    []
  )

  const getButtonLabel = (type: 'subject' | 'body' | 'recipient', state: CopyState) => {
    if (state === 'success') {
      if (type === 'subject') return 'Subject Copied to Clipboard!'
      if (type === 'body') return 'Body Copied to Clipboard!'
      return 'Recipient Copied to Clipboard!'
    }

    if (state === 'error') {
      if (type === 'subject') return 'Unable to copy subject. Please copy manually.'
      if (type === 'body') return 'Unable to copy body. Please copy manually.'
      return 'Unable to copy recipient. Please copy manually.'
    }

    if (type === 'subject') return 'Copy Email Subject'
    if (type === 'body') return 'Copy Email Body'
    return 'Copy Email Recipient'
  }

  const baseButtonClasses =
    'w-full font-semibold py-2 px-4 rounded focus:outline-none text-white transition-colors duration-200 focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-900 disabled:opacity-70 disabled:cursor-not-allowed'

  const getButtonVariantClasses = (state: CopyState) => {
    switch (state) {
      case 'success':
        return 'bg-emerald-600 hover:bg-emerald-500 focus:bg-emerald-500 focus:ring-emerald-400'
      case 'error':
        return 'bg-red-600 hover:bg-red-500 focus:bg-red-500 focus:ring-red-400'
      default:
        return 'bg-slate-500 hover:bg-slate-400 focus:bg-slate-400 focus:ring-slate-400'
    }
  }

  const emailAddress = cantFindLocationEmailAddress(
    formValues.address.country?.value as CountryCode
  )

  return (
    <div className="rounded-md mt-5 border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 p-4">
      <p className="text-sm text-slate-700 dark:text-slate-200">
        If your email app did not open, copy the text below and email it to
        <span className="font-bold">{` ${emailAddress}`}</span>
      </p>

      <div className="flex flex-col items-center justify-center gap-3 mt-3">
        <button
          type="button"
          className={`${baseButtonClasses} ${getButtonVariantClasses(recipientCopyState)}`}
          onClick={() => copyWithFeedback(emailAddress, 'recipient')}
          disabled={recipientCopyState === 'success'}
          aria-live="polite"
        >
          {getButtonLabel('recipient', recipientCopyState)}
        </button>

        <button
          type="button"
          className={`${baseButtonClasses} ${getButtonVariantClasses(subjectCopyState)}`}
          onClick={() => copyWithFeedback(cantFindLocationEmailSubjectText, 'subject')}
          disabled={subjectCopyState === 'success'}
          aria-live="polite"
        >
          {getButtonLabel('subject', subjectCopyState)}
        </button>

        <button
          type="button"
          className={`${baseButtonClasses} ${getButtonVariantClasses(bodyCopyState)}`}
          onClick={() => {
            const emailBody = cantFindLocationMailToBody({
              venueName: formValues.venueName,
              address: formValues.address,
              contactName: formValues.contact.contactName,
              contactEmail: formValues.contact.contactEmail,
              contactPhone: formValues.contact.contactPhone,
              additionalNotes: formValues.additionalNotes,
              includeKegCollection: formValues.includeKegCollection,
              collectionRequest: formValues.collectionRequest,
            })
            copyWithFeedback(emailBody, 'body')
          }}
          disabled={bodyCopyState === 'success'}
          aria-live="polite"
        >
          {getButtonLabel('body', bodyCopyState)}
        </button>
      </div>
    </div>
  )
}
